import { defineStore } from "pinia";
import type { AxiosRequestConfig } from "axios";
import api from "@/_helpers/api";
import router from "@/_helpers/router";
import { axiosInstance } from "@/plugins/https";
import { StoreModule } from "@/constants/store";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { i18n } from "@/plugins/i18n";
import { useSnackbarStore } from "@/_store/snackbar.module";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import {
  BackupAndRestoreStatus,
  type BackupChild,
  type RetentionType,
  type KeepitWorkload,
} from "@/constants/cloud-backup.ts";
import type { Pagination } from "@/types";

export interface DataCenterDto {
  code: string;
  country: string;
  city: string;
}

export interface KeepitAccountResponse {
  keepitAccountId: string;
  status: string;
  dataCenter: string;
}

export interface KeepitAutologinRequest {
  workspaceId: string;
  logoutRedirectPath: string;
}

export interface KeepitAutologinResponse {
  redirectUrl: string;
}

export interface CreateMspBackupAccountPayload {
  dataCenter: string;
}

export interface BackupAndRestoreStatusMetadata {
  dataCenter?: string;
  parentWorkspaceId?: string;
}

export interface BackupAndRestoreStatusResponse {
  status: BackupAndRestoreStatus;
  metadata: BackupAndRestoreStatusMetadata;
}

export interface BackupDescendant {
  workspaceId: string;
  name: string;
}

export interface CreateBackupChildPayload {
  workspaceId: string | null;
  workloadIds: KeepitWorkload[];
  limit: number | null;
  retentionType: RetentionType;
}

export interface UpdateBackupChildPropertiesPayload {
  workloadIds: KeepitWorkload[];
  limit: number;
}

export interface BackupChildrenResponse {
  items: BackupChild[];
  total: number;
}

export interface BackupAndRestoreState {
  status: BackupAndRestoreStatus | null;
  metadata: BackupAndRestoreStatusMetadata;
  children: BackupChild[];
  totalChildren: number;
  pagination: Pagination;
}

export const defaultBackupAndRestoreState: BackupAndRestoreState = {
  status: BackupAndRestoreStatus.NOT_CONNECTED,
  metadata: {},
  children: [],
  totalChildren: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
};

export const useMspCloudBackupStore = defineStore(StoreModule.MSP_CLOUD_BACKUP, {
  state: (): BackupAndRestoreState => ({
    ...defaultBackupAndRestoreState,
    pagination: { ...defaultBackupAndRestoreState.pagination },
  }),
  actions: {
    setPagination(pagination: Pagination): void {
      this.pagination = pagination;
    },
    resetPagination(): void {
      this.pagination = { ...defaultBackupAndRestoreState.pagination };
    },
    async getStatus() {
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreStatus,
        method: "GET",
      };
      try {
        const { data } = await axiosInstance.request<BackupAndRestoreStatusResponse>(request);
        this.status = data.status;
        this.metadata = data.metadata ?? {};
      } catch (err) {
        console.error(err);
        this.status = BackupAndRestoreStatus.FAILED;
        this.metadata = {};
      }
    },
    async getDescendants(): Promise<BackupDescendant[]> {
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreDescendants,
        method: "GET",
      };
      try {
        const { data } = await axiosInstance.request<BackupDescendant[]>(request);
        return data ?? [];
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    async getChildren() {
      // Filtering and pagination are done server-side: the child-workspace filters are
      // read from the filters store and forwarded, together with the current page, as
      // query params.
      const filters = useFiltersStore().filters[FilterContext.BACKUP_AND_RESTORE_CHILD_WORKSPACES];
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreChildren,
        method: "GET",
        params: {
          search: filters.search || undefined,
          connector: filters.cloudApps.length ? filters.cloudApps.join(",") : undefined,
          connectionStatus: filters.connectionStatus ?? undefined,
          healthStatus: filters.healthStatus ?? undefined,
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
        },
      };
      try {
        const { data } = await axiosInstance.request<BackupChildrenResponse>(request);
        this.children = data.items ?? [];
        this.totalChildren = data.total;
      } catch (err) {
        console.error(err);
        this.children = [];
        this.totalChildren = 0;
      }
    },
    async getDataCenters(): Promise<DataCenterDto[]> {
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreDataCenters,
        method: "GET",
      };
      try {
        const { data } = await axiosInstance.request<DataCenterDto[]>(request);
        return data ?? [];
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    async createChild(data: CreateBackupChildPayload) {
      const request: AxiosRequestConfig<CreateBackupChildPayload> = {
        ...api.backupAndRestoreCreateChild,
        data,
      };
      try {
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.backupAndRestore.customerAccountCreationInProgress")
        );
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.backupAndRestore.customerAccountCreated")
        );
      } catch (e) {
        console.error(e);
      }
    },
    async updateChildProperties(
      childWorkspaceId: string,
      data: UpdateBackupChildPropertiesPayload
    ) {
      const request: AxiosRequestConfig<UpdateBackupChildPropertiesPayload> = {
        ...api.backupAndRestoreChildProperties(childWorkspaceId),
        data,
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.backupAndRestore.customerAccountUpdated")
        );
      } catch (e) {
        console.error(e);
      }
    },
    // Keepit SSO autologin for a workspace. Mirrors the MDM `loginToMdm` flow:
    // request a one-time URL from the backend, then open it in the browser.
    // Logging out of the Keepit console returns the user to the page they opened it from.
    async autologin(workspaceId: string, openInNewTab = true) {
      try {
        const request: AxiosRequestConfig<KeepitAutologinRequest> = {
          ...api.backupAndRestoreAutologin,
          data: { workspaceId, logoutRedirectPath: router.currentRoute.value.fullPath },
        };
        const { data } = await axiosInstance.request<KeepitAutologinResponse>(request);
        window.open(data.redirectUrl, openInNewTab ? "_blank" : "_self");
      } catch (err) {
        console.error(err);
      }
    },
    async createMspAccount(data: DialogDataConfig<CreateMspBackupAccountPayload>) {
      const request: AxiosRequestConfig = {
        ...api.backupAndRestoreCreateChannel,
        data: data.item,
      };
      await axiosInstance.request<KeepitAccountResponse>(request);
      useSnackbarStore().addGenericSuccess(
        i18n.global.t("snackbar.messages.backupAndRestore.accountCreated")
      );
    },
  },
});
